/**
 * People service, REST over `/planning/:project_id/people*`.
 *
 * A person belongs to the workspace, not to one project: the same list comes
 * back whichever project of the workspace is active. Nothing is cached here;
 * the API is the source of truth and the screen reloads after every change.
 *
 * Contract: alfa2267/ans-backend issue 308. The list answer is read as
 * `{ people: [...] }`, and a bare array or a `{ data: [...] }` envelope is
 * accepted too, so the exact wrapper does not matter.
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, planningPath } from './PlanningApiClient';
import type { UserRole } from '../types/User';
import type { PeopleStatus, Person, PersonInput } from '../types/People';

const peoplePath = (suffix = '') => planningPath(`/people${suffix}`);

function normalizePerson(raw: Person): Person {
  return {
    ...raw,
    login: raw.login ?? null,
    projects: Array.isArray(raw.projects) ? raw.projects : [],
  };
}

export async function fetchPeople(status: PeopleStatus = 'active'): Promise<Person[]> {
  const res = await apiGet<Person[] | { people?: Person[]; data?: Person[] } | null>(peoplePath(`?status=${status}`));
  const rows = Array.isArray(res) ? res : res?.people ?? res?.data ?? [];
  return rows.map(normalizePerson);
}

/** Text from a form: trimmed, and empty becomes null so a field can be cleared. */
const clean = (v: string | null | undefined): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t === '' ? null : t;
};

export function createPerson(input: PersonInput): Promise<Person> {
  const body: Record<string, unknown> = { name: input.name.trim() };
  for (const key of ['email', 'phone', 'whatsapp', 'notes'] as const) {
    if (input[key] !== undefined) body[key] = clean(input[key]);
  }
  return apiPost<Person>(peoplePath(), body);
}

export function updatePerson(id: string, input: Partial<PersonInput>): Promise<Person> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name.trim();
  if (input.email !== undefined) body.email = clean(input.email)?.toLowerCase() ?? null;
  for (const key of ['phone', 'whatsapp', 'notes'] as const) {
    if (input[key] !== undefined) body[key] = clean(input[key]);
  }
  return apiPut<Person>(peoplePath(`/${encodeURIComponent(id)}`), body);
}

/** Owner only. The person needs an e-mail first (the API refuses otherwise). */
export function giveLogin(id: string, role: UserRole): Promise<Person> {
  return apiPost<Person>(peoplePath(`/${encodeURIComponent(id)}/login`), { role });
}

export function addPersonToProject(id: string, projectId: string, teams: string[] = []): Promise<Person> {
  // The API reads the target as target_project_id; a body project_id must equal the URL project.
  const body: Record<string, unknown> = { target_project_id: projectId };
  if (teams.length > 0) body.teams = teams;
  return apiPost<Person>(peoplePath(`/${encodeURIComponent(id)}/projects`), body);
}

/** Moves that project's team member to the trash. Never a hard delete. */
export function removePersonFromProject(id: string, projectId: string): Promise<unknown> {
  return apiDelete<unknown>(peoplePath(`/${encodeURIComponent(id)}/projects/${encodeURIComponent(projectId)}`));
}

export const archivePerson = (id: string) => apiPatch<Person>(peoplePath(`/${encodeURIComponent(id)}/archive`), {});
export const restorePerson = (id: string) => apiPatch<Person>(peoplePath(`/${encodeURIComponent(id)}/restore`), {});
